import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useField } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandedJson, FormControls } from 'components';
import { AppConfigArea, AppConfigKey } from 'enums';
import { TranslationNamespace } from 'i18n';
import { appConfigUtils } from 'utils';

import { ConfigOverrideDialog } from './ConfigOverrideDialog';

type Props = {
  fieldPath: string;
  providerType?: string;
};

type ConfigOverride = {
  key: string;
  value: any;
};

export const ConfigOverridesField: React.FC<Props> = ({
  fieldPath,
  providerType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const [field, , helpers] = useField<Record<string, any>>(
    `${fieldPath}.configOverrides`,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<
    ConfigOverride | undefined
  >(undefined);
  const [expanded, setExpanded] = useState(false);

  const configOverridesArray = useMemo(
    () =>
      !field.value
        ? []
        : Object.entries(field.value).map(([key, value]) => ({ key, value })),
    [field.value],
  );

  const existingKeys = useMemo(
    () => configOverridesArray.map((override) => override.key),
    [configOverridesArray],
  );

  const area = useMemo(
    () =>
      fieldPath === 'payinConfig'
        ? AppConfigArea.PayinP2PProvider
        : AppConfigArea.PayoutP2PProvider,
    [fieldPath],
  );

  const handleAdd = useCallback(() => {
    setEditingOverride(undefined);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((override: ConfigOverride) => {
    setEditingOverride(override);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    (key: string) => {
      const newValue = { ...field.value };
      delete newValue[key];
      helpers.setValue(newValue);
    },
    [field.value, helpers],
  );

  const handleDialogClose = useCallback(
    (result: { ok: boolean; data?: ConfigOverride }) => {
      if (result.ok && result.data) {
        const newValue = { ...field.value };

        if (editingOverride && editingOverride.key !== result.data.key) {
          delete newValue[editingOverride.key];
        }

        newValue[result.data.key] = result.data.value;
        helpers.setValue(newValue);
      }
      setDialogOpen(false);
      setEditingOverride(undefined);
    },
    [editingOverride, field.value, helpers],
  );

  if (!providerType) {
    return null;
  }

  return (
    <Fragment>
      <Divider>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" sx={{ mr: 1 }}>
            {t('fields.config_overrides')}
          </Typography>
          <Tooltip
            title={t('fields.config_overrides_description')}
            placement="top"
          >
            <InfoOutlined
              sx={{ fontSize: 'small', color: 'text.secondary', mr: 1 }}
            />
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ p: 0.5 }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Divider>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <FormControls>
            {configOverridesArray.map((override) => (
              <Paper key={override.key} sx={{ px: 4, py: 4 }}>
                <FormControls>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {appConfigUtils.getKeyLabel(override.key as AppConfigKey)}
                    </Typography>
                    <div className="tw-flex tw-items-center">
                      <IconButton
                        onClick={() => handleEdit(override)}
                        size="small"
                      >
                        <EditIcon color="action" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(override.key)}
                        size="small"
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </div>
                  </div>
                  <ExpandedJson json={override.value} />
                </FormControls>
              </Paper>
            ))}

            <Button variant="outlined" onClick={handleAdd} fullWidth>
              {t('fields.config_overrides_add_button')}
            </Button>
          </FormControls>
        </Box>
      </Collapse>

      <ConfigOverrideDialog
        open={dialogOpen}
        data={editingOverride}
        area={area}
        providerType={providerType}
        existingKeys={existingKeys.filter(
          (key) => key !== editingOverride?.key,
        )}
        onClose={handleDialogClose}
      />
    </Fragment>
  );
};
